import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  usePreboardingConflicts,
  useResolvePreboardingConflict,
} from '@/hooks/use-preboarding-conflicts';
import type { PreboardingConflictDto } from '@hygge/shared';
import { CheckCircle2, Loader2 } from 'lucide-react';

interface PreboardingConflictModalProps {
  employeeId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// HR review of pre-boarding answers that differ from existing profile data.
// Each row shows current vs submitted; Overwrite applies the submitted value,
// Keep discards it. Both mark the conflict resolved.
export function PreboardingConflictModal({
  employeeId,
  open,
  onOpenChange,
}: PreboardingConflictModalProps) {
  const { data: conflicts, isLoading } = usePreboardingConflicts(employeeId, open);
  const resolve = useResolvePreboardingConflict(employeeId);

  const pending = (conflicts ?? []).filter((c) => c.status === 'PENDING');
  const resolved = (conflicts ?? []).filter((c) => c.status === 'RESOLVED');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[640px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Review submitted data</DialogTitle>
          <DialogDescription>
            These pre-boarding answers differ from what&apos;s already on the profile. Choose
            whether to overwrite the existing value or keep it.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-5 w-5 animate-spin text-hygge" />
          </div>
        ) : pending.length === 0 && resolved.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            No submitted data to review.
          </p>
        ) : (
          <div className="space-y-3">
            {pending.map((c) => (
              <ConflictRow
                key={c.id}
                conflict={c}
                busy={resolve.isPending && resolve.variables?.id === c.id}
                onResolve={(resolution) => resolve.mutate({ id: c.id, resolution })}
              />
            ))}

            {resolved.length > 0 && (
              <div className="pt-2">
                <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Resolved
                </p>
                <div className="space-y-2">
                  {resolved.map((c) => (
                    <div
                      key={c.id}
                      className="flex items-center justify-between gap-3 rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-sm"
                    >
                      <span className="font-medium">{c.fieldLabel}</span>
                      <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <CheckCircle2 className="h-3.5 w-3.5 text-hygge" />
                        {c.resolution === 'OVERWRITE' ? 'Overwritten' : 'Kept existing'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ConflictRow({
  conflict,
  busy,
  onResolve,
}: {
  conflict: PreboardingConflictDto;
  busy: boolean;
  onResolve: (resolution: 'OVERWRITE' | 'KEEP') => void;
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-3.5">
      <p className="mb-2.5 text-sm font-semibold">{conflict.fieldLabel}</p>
      <div className="grid grid-cols-2 gap-3">
        <ValueBox label="Current value" value={conflict.currentValue} />
        <ValueBox label="Submitted value" value={conflict.submittedValue} highlight />
      </div>
      <div className="mt-3 flex justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={busy}
          onClick={() => onResolve('KEEP')}
        >
          Keep existing
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={busy}
          className="bg-cta hover:bg-cta/90 text-cta-foreground"
          onClick={() => onResolve('OVERWRITE')}
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Overwrite'}
        </Button>
      </div>
    </div>
  );
}

function ValueBox({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string | null;
  highlight?: boolean;
}) {
  return (
    <div className="space-y-1">
      <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p
        className={`break-words rounded-md border px-2.5 py-1.5 text-sm ${
          highlight ? 'border-hygge/30 bg-hygge/5' : 'border-border bg-muted/30'
        }`}
      >
        {value && value.trim() !== '' ? value : <span className="text-muted-foreground">—</span>}
      </p>
    </div>
  );
}
